import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { useDeviceMetrics } from '../../hooks/useDeviceMetrics';
import type { Device, MetricPeriod, MetricPanelData, MetricGroupData } from '../types';
import { METRIC_PERIOD_CONFIG, getCardBorder, CARD_BORDER_RADIUS, GRAFANA_DASHBOARD_MAP } from '../types';

interface MetricsPreviewProps {
  device: Device;
  grafanaUrl: string | null;
}

// Chart color palette
const CHART_COLORS = [
  '#1976d2', // blue
  '#2e7d32', // green
  '#ed6c02', // orange
  '#9c27b0', // purple
  '#d32f2f', // red
  '#0288d1', // light blue
  '#c2185b', // pink
  '#689f38', // light green
];

// ---------------------------------------------------------------------------
// Unit Formatting
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024));
  const idx = Math.min(i, units.length - 1);
  return `${(bytes / Math.pow(1024, idx)).toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
}

function formatBytesPerSec(bytes: number): string {
  return `${formatBytes(bytes)}/s`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatSeconds(seconds: number): string {
  if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${(seconds / 60).toFixed(1)}min`;
}

function formatValue(value: number | undefined, unit: string): string {
  if (value === undefined || value === null || isNaN(value)) return '--';
  switch (unit) {
    case 'percent':
      return formatPercent(value);
    case 'bytes':
      return formatBytes(value);
    case 'bytes/s':
      return formatBytesPerSec(value);
    case 'seconds':
      return formatSeconds(value);
    case 'ops/s':
      return `${value.toFixed(1)}/s`;
    default:
      return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }
}

function formatYAxis(value: number, unit: string): string {
  switch (unit) {
    case 'percent':
      return `${value.toFixed(0)}%`;
    case 'bytes':
      return formatBytes(value);
    case 'bytes/s':
      return formatBytesPerSec(value);
    case 'seconds':
      return formatSeconds(value);
    case 'ops/s':
      return `${value.toFixed(0)}`;
    default:
      return value.toFixed(0);
  }
}

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------

function StatCard({ panel }: { panel: MetricPanelData }) {
  const theme = useTheme();
  const value = panel.currentValue;
  const unit = panel.def.unit;
  const thresholds = panel.def.thresholds;

  // Determine color from thresholds (sorted desc by value)
  let color = theme.palette.success.main;
  if (thresholds && value !== undefined) {
    const sorted = [...thresholds].sort((a, b) => b.value - a.value);
    for (const t of sorted) {
      if (value >= t.value) {
        color = t.color;
        break;
      }
    }
  }

  // Sparkline data: use the first legend key
  const sparkKey = panel.legends[0];
  const sparkData = sparkKey ? panel.series : [];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: getCardBorder(theme),
        borderRadius: CARD_BORDER_RADIUS,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      <Typography variant="caption" color="text.secondary" noWrap>
        {panel.def.title}
      </Typography>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, color, mt: 0.5, fontSize: '1.75rem' }}
      >
        {formatValue(value, unit)}
      </Typography>
      {sparkKey && sparkData.length > 1 && (
        <Box sx={{ width: '100%', height: 40, mt: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`spark-${panel.def.title}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey={sparkKey}
                stroke={color}
                fill={`url(#spark-${panel.def.title})`}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Time Series Chart
// ---------------------------------------------------------------------------

function TimeSeriesChart({ panel, period }: { panel: MetricPanelData; period: MetricPeriod }) {
  const theme = useTheme();
  const unit = panel.def.unit;
  const isStacked = panel.def.chartType === 'stacked-area';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: getCardBorder(theme),
        borderRadius: CARD_BORDER_RADIUS,
        mb: 2,
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
        {panel.def.title}
      </Typography>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={panel.series} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <defs>
            {panel.legends.map((legend, i) => (
              <linearGradient
                key={legend}
                id={`gradient-${panel.def.title}-${i}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={CHART_COLORS[i % CHART_COLORS.length]}
                  stopOpacity={isStacked ? 0.6 : 0.2}
                />
                <stop
                  offset="95%"
                  stopColor={CHART_COLORS[i % CHART_COLORS.length]}
                  stopOpacity={0}
                />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis
            dataKey="time"
            type="number"
            domain={['dataMin', 'dataMax']}
            scale="time"
            tickFormatter={(ts) => {
              const d = new Date(ts);
              return period === '1h' || period === '6h'
                ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
                : period === '24h'
                  ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
                  : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
            }}
            tick={{ fontSize: 11 }}
            stroke={theme.palette.text.secondary}
          />
          <YAxis
            tickFormatter={(v) => formatYAxis(v, unit)}
            tick={{ fontSize: 11 }}
            stroke={theme.palette.text.secondary}
            width={60}
          />
          <RechartsTooltip
            labelFormatter={(ts) => new Date(ts as number).toLocaleString()}
            formatter={(value: number, name: string) => [formatValue(value, unit), name]}
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 8,
              fontSize: 12,
            }}
          />
          {panel.legends.map((legend, i) => (
            <Area
              key={legend}
              type="monotone"
              dataKey={legend}
              stroke={CHART_COLORS[i % CHART_COLORS.length]}
              fill={`url(#gradient-${panel.def.title}-${i})`}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, strokeWidth: 1 }}
              stackId={isStacked ? '1' : undefined}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Metric Group Section (collapsible)
// ---------------------------------------------------------------------------

function MetricGroupSection({
  group,
  period,
}: {
  group: MetricGroupData;
  period: MetricPeriod;
}) {
  const [expanded, setExpanded] = useState(true);

  // Separate stat panels from time series panels
  const statPanels = group.panels.filter((p) => p.def.chartType === 'stat');
  const chartPanels = group.panels.filter((p) => p.def.chartType !== 'stat');

  // If the group is "Overview" (stat cards only), render as a grid
  if (group.group === 'Overview') {
    return (
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(auto-fit, minmax(150px, 1fr))',
            },
            gap: 2,
          }}
        >
          {statPanels.map((panel) => (
            <StatCard key={panel.def.title} panel={panel} />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          py: 1,
          userSelect: 'none',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ExpandLessIcon fontSize="small" color="action" />
        ) : (
          <ExpandMoreIcon fontSize="small" color="action" />
        )}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ ml: 0.5 }}>
          {group.group}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        {statPanels.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 2,
              mb: 2,
            }}
          >
            {statPanels.map((panel) => (
              <StatCard key={panel.def.title} panel={panel} />
            ))}
          </Box>
        )}
        {chartPanels.map((panel) => (
          <TimeSeriesChart key={panel.def.title} panel={panel} period={period} />
        ))}
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Loading Skeleton
// ---------------------------------------------------------------------------

function MetricsSkeleton() {
  return (
    <Box>
      {/* Stat cards skeleton */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 2,
          mb: 3,
        }}
      >
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" height={100} />
        ))}
      </Box>
      {/* Chart skeletons */}
      {[1, 2, 3].map((i) => (
        <Skeleton key={`chart-${i}`} variant="rounded" height={240} sx={{ mb: 2 }} />
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function MetricsPreview({ device, grafanaUrl }: MetricsPreviewProps) {
  const theme = useTheme();
  const [period, setPeriod] = useState<MetricPeriod>('1h');
  const { groups, loading, error, lastUpdated } = useDeviceMetrics(device, period);

  const handlePeriodChange = (
    _: React.MouseEvent<HTMLElement>,
    newPeriod: MetricPeriod | null,
  ) => {
    if (newPeriod) setPeriod(newPeriod);
  };

  const handleOpenGrafana = () => {
    if (grafanaUrl) {
      window.open(grafanaUrl, '_blank');
    }
  };

  const hasTemplates = !!device.prometheusJob && (
    device.prometheusJob in
      { node_exporter: 1, windows_exporter: 1, process_exporter: 1, sql_exporter: 1, vmware_esxi: 1, 'cansense-push': 1 }
  );

  return (
    <Paper
      elevation={0}
      sx={{
        border: getCardBorder(theme),
        borderRadius: CARD_BORDER_RADIUS,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          px: 2,
          borderBottom: getCardBorder(theme),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Metrics
          </Typography>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Updated {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {grafanaUrl && (
            <Tooltip title="Open in Grafana">
              <IconButton size="small" onClick={handleOpenGrafana}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25, fontSize: '0.75rem' } }}
          >
            {(Object.keys(METRIC_PERIOD_CONFIG) as MetricPeriod[]).map((p) => (
              <ToggleButton key={p} value={p}>
                {p}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
        {loading && groups.length === 0 ? (
          <MetricsSkeleton />
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => setPeriod(period)}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        ) : !hasTemplates ? (
          /* Unknown exporter type */
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: device.status === 'up' ? 'success.main' : 'error.main',
                mb: 1,
              }}
            >
              {device.status === 'up' ? 'UP' : 'DOWN'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No metric templates available for exporter type "{device.prometheusJob || 'unknown'}"
            </Typography>
            {grafanaUrl && (
              <Button
                variant="outlined"
                size="small"
                endIcon={<OpenInNewIcon />}
                onClick={handleOpenGrafana}
              >
                Open in Grafana
              </Button>
            )}
          </Box>
        ) : groups.length === 0 && !loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No metrics available for the selected period
            </Typography>
          </Box>
        ) : (
          groups.map((group) => (
            <MetricGroupSection key={group.group} group={group} period={period} />
          ))
        )}
      </Box>
    </Paper>
  );
}

export default MetricsPreview;
